import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahCompanyExternalServices } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

/**
 * One approved service the import wrote, in either the created or the updated list.
 *
 * `id` is a composite key (`client_id|service_uuid|start_date`) because Services Australia sends no
 * approval id and TableView needs a unique `id` on every row. `start_date` / `end_date` are the
 * STORED dates: an approval that spans a service-period split is written as one row per period, so
 * they can be narrower than what Services Australia sent. Both are bare calendar dates
 * (YYYY-MM-DD), `end_date` is null for an open-ended approval.
 */
export interface ImportApprovedServiceItem {
  id: string;
  client_id: number;
  client_name: string;
  sah_service_id: string;
  // The TurnPoint service name for the period the row was written against.
  service_name: string;
  // 'Ongoing' or 'Short Term'.
  approval_type: string | null;
  start_date: string;
  end_date: string | null;
}

/**
 * An approved service the import fetched but did not write, with the first reason it could not be
 * written. Dates are as Services Australia sent them. `client_name` is null whenever no TurnPoint
 * client matched, and `service_name` is the Services Australia service text.
 */
export interface ImportApprovedServiceSkipped {
  care_recipient_id: string | null;
  client_name: string | null;
  sah_service_id: string | null;
  service_name: string | null;
  approval_type: string | null;
  start_date: string | null;
  end_date: string | null;
  reason: string;
}

/**
 * The `data` payload of `POST .../companies/external/services/import`, after `unwrap()`.
 * `truncated` means the server hit its defensive page cap, not that anything failed.
 */
export interface ImportApprovedServicesResponse {
  fetched: number;
  truncated: boolean;
  created: number;
  updated: number;
  created_items: ImportApprovedServiceItem[];
  updated_items: ImportApprovedServiceItem[];
  skipped: ImportApprovedServiceSkipped[];
}

/**
 * Company-scoped Support at Home approved services actions (TAV-3420).
 *
 * A slice of its own, like sahCompanyBudgetsApi, because the route lives under `/v2/sah/companies/...`.
 *
 * This slice MUST stay registered in `supportAtHomeApis` (reducers/index.ts) - store.ts reads that
 * array for both reducers and middleware, and without it the hook silently no-ops.
 */
export const sahCompanyServicesApi = createApi({
  reducerPath: 'sahCompanyServicesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahCompanyExternalServices }),
  tagTypes: ['sah-company-services'],
  endpoints: (builder) => ({
    importApprovedServices: builder.mutation<ImportApprovedServicesResponse, void>({
      query: () => ({ url: '/import', method: 'POST', body: {} }),
      // No invalidatesTags: the grouped services list is a classic AsyncThunk slice, so the modal's
      // onClose calls the parent's load() instead.
      transformResponse: (payload) => unwrap<ImportApprovedServicesResponse>(payload),
    }),
  }),
});

export const { useImportApprovedServicesMutation } = sahCompanyServicesApi;
